import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { SelectItem, TmdxSelect } from '@ds';
import { BaseAviaLegSchemaValues } from '@lp/features/RoutePlanner/components/RouteLegForm/validation';
import { useAirports } from '@lp/features/RoutePlanner/hooks/useAirports';
import { AirportListItemType } from '@lp/features/RoutePlanner/types';
import { CUSTOM_AIRPORTS_QUERY, DEFAULT_AIRPORTS_QUERY } from '@lp/features/RoutePlanner/config';
import { getFboName } from '@lp/features/RoutePlanner/components/RouteLegForm/utils';
import { AirportLocationContext } from '@lp/features/RoutePlanner/components/RouteLegForm/context/AirportLocationContext';
import { useLegFormContext } from '@lp/features/RoutePlanner/components/RouteLegForm/context/LegFormContext';
import { renderSelectItem } from '@/utils/helpers';
import { useWatchFields } from '@/hooks/useWatchFields';
import { LegTypeEnum, RouteLegTypeEnum, SegmentTypeEnum } from '@/api/apis/route';
import { GROUND_LEG_TYPES } from '@/utils/route';

const LOCATION_LABEL_MAP = {
  departureLocation: 'Departure',
  arrivalLocation: 'Arrival',
};

export const AirportLocation = () => {
  const { isMainDataEdit, runSubmitBlockingTransaction } = useLegFormContext();
  const { locationType, currentFbos, setCurrentFbos, handleSelectedAirport } = useContext(AirportLocationContext);
  const [defaultAirport, setDefaultAirport] = useState<AirportListItemType | null>(null);
  const {
    control,
    setValue,
    formState: { defaultValues },
  } = useFormContext<BaseAviaLegSchemaValues>();

  const {
    [locationType]: locationValue,
    legType,
    formType,
    segmentType,
  } = useWatchFields<BaseAviaLegSchemaValues>([locationType, 'legType', 'formType', 'segmentType'] as const);

  const isGroundLeg = GROUND_LEG_TYPES.includes(legType);
  const isCustomSegment = segmentType === SegmentTypeEnum.custom;
  const airportsQuery = isCustomSegment && isGroundLeg ? CUSTOM_AIRPORTS_QUERY : DEFAULT_AIRPORTS_QUERY;

  const {
    airportsList,
    isAirportsListLoading,
    onAirportSearch,
    airportsScrollerRef,
    onAirportSelectChange,
    searchValue,
    setAirportsAutocompleteOpen,
    getSelectedAirport,
  } = useAirports({
    selectedId: locationValue?.id,
    selectedValue: defaultValues?.[locationType]?.displayName,
    setValue: (v: string) => setValue(`${locationType}.id`, v, { shouldValidate: true }),
    filters: airportsQuery,
  });

  useEffect(() => {
    return () => {
      setCurrentFbos([]);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!defaultAirport && defaultValues?.[locationType]?.id) {
      getSelectedAirport(defaultValues?.[locationType]?.id).then((airport) => {
        if (airport) {
          setDefaultAirport(airport);
          setCurrentFbos(airport.fbos || []);
        }
      });
    }
  }, [defaultAirport, defaultValues, getSelectedAirport, locationType, setCurrentFbos]);

  const onAirportListOpenChange = useCallback(
    (isOpen: boolean) => {
      if (!isOpen && !airportsList.length) {
        onAirportSearch('');
      }
      setAirportsAutocompleteOpen(true);
    },
    [airportsList.length, onAirportSearch, setAirportsAutocompleteOpen],
  );

  const handleAirportsSelectChange = useCallback(
    async (id: string) => {
      await runSubmitBlockingTransaction(async () => {
        const airport = await onAirportSelectChange(id);

        if (airport) {
          await handleSelectedAirport(airport);
        }
      });
    },
    [runSubmitBlockingTransaction, handleSelectedAirport, onAirportSelectChange],
  );

  const handleFboSelectChange = useCallback(
    (id: string) => {
      setValue(`${locationType}.fboId`, id);

      const selectedFbo = currentFbos.find((fbo) => fbo.id === id);

      if (selectedFbo) {
        setValue(`${locationType}.fboAddressId`, selectedFbo.addresses[0].id || 0);
      }
    },
    [currentFbos, locationType, setValue],
  );

  const isCommercial = legType === RouteLegTypeEnum.CommercialFlightAviaLeg;
  const isGround = formType !== LegTypeEnum.air;

  const airportAviaLegLabel = isCommercial
    ? `${LOCATION_LABEL_MAP[locationType]} Airport *`
    : `${LOCATION_LABEL_MAP[locationType]} *`;

  const airportInputLabel = isGround ? 'Airport *' : airportAviaLegLabel;

  const defaultAirportsList = useMemo(() => {
    const isAirportInList = airportsList.some((a) => a.value === locationValue?.id);

    if (
      !isAirportInList &&
      defaultValues?.[locationType]?.id &&
      defaultValues?.[locationType]?.id === locationValue?.id
    ) {
      return defaultAirport ? [defaultAirport] : [];
    }

    return airportsList;
  }, [airportsList, defaultAirport, defaultValues, locationType, locationValue?.id]);

  return (
    <>
      <Controller
        name={`${locationType}.id`}
        control={control}
        render={({ field: { ref, value, onChange, ...field }, fieldState: { invalid } }) => (
          <TmdxSelect
            label={airportInputLabel}
            placeholder="Choose an option"
            aria-label={airportInputLabel}
            scrollRef={airportsScrollerRef}
            items={defaultAirportsList}
            isLoading={isAirportsListLoading}
            isInvalid={invalid}
            isDisabled={!isMainDataEdit}
            disallowEmptySelection
            onOpenChange={onAirportListOpenChange}
            selectedKeys={value ? [value] : []}
            disabledKeys={value ? [value] : []}
            onSelectionChange={(key) => handleAirportsSelectChange(key.currentKey as string)}
            onSearchValue={onAirportSearch}
            searchValue={searchValue}
            showCheckmarkIcon
            {...field}
          >
            {renderSelectItem}
          </TmdxSelect>
        )}
      />
      {!isCommercial && (
        <Controller
          name={`${locationType}.fboId`}
          control={control}
          render={({ field: { ref, value, onChange, ...field }, fieldState: { invalid } }) => (
            <TmdxSelect
              placeholder="Choose an option"
              label="FBO Address *"
              aria-label="FBO Address"
              selectedKeys={value ? [value] : []}
              isInvalid={invalid}
              disallowEmptySelection
              isDisabled={!isMainDataEdit || !currentFbos.length}
              showCheckmarkIcon
              onSelectionChange={(key) => handleFboSelectChange(key.currentKey as string)}
              {...field}
            >
              {currentFbos.map((e) => (
                <SelectItem key={e.id as string}>{getFboName(e)}</SelectItem>
              ))}
            </TmdxSelect>
          )}
        />
      )}
    </>
  );
};
